import React, { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import Search from "../../../assets/images/search.svg";
import "./ReportedPlates.scss";
import DataTable from "react-data-table-component";
import { Link } from "react-router-dom";
import { getPlatesByStatus, deletePlate } from "../../../services/platesService";
import { ConfirmModal } from "../../../components/confirm-modal/ConfirmModal";
import {
  flattenProfilesExcludingPlateStatuses,
  REPORTED_PLATES_EXCLUDED_STATUSES,
  getPlateCaption,
  getPlateDisplayImageUrl,
  getPlateLocationCountry,
  getPlateLocationState,
  getPlateNumber,
  getProfileDisplayName,
  getProfileEmail,
  getProfileUserImage,
  mapPlateStatusLabel,
} from "../../../utils/platesProfileHelpers";

const REASON_PREVIEW_MAX_CHARS = 90

function truncateReasonPreview(text) {
  const full = text == null ? "" : String(text)
  if (full.length <= REASON_PREVIEW_MAX_CHARS) {
    return { preview: full, isTruncated: false }
  }
  return {
    preview: `${full.slice(0, REASON_PREVIEW_MAX_CHARS)}…`,
    isTruncated: true,
  }
}

function ReportedPlates() {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState("");
  const [plateToDelete, setPlateToDelete] = useState(null);

  const deletePlateMutation = useMutation({
    mutationFn: (plateId) => deletePlate(plateId),
    onSuccess: (data) => {
      toast.success(data?.message || "Plate deleted successfully", {
        position: "top-right",
        autoClose: 3000,
      });
      queryClient.invalidateQueries({ queryKey: ["plates"] });
      queryClient.invalidateQueries({ queryKey: ["plates-by-status"] });
      queryClient.invalidateQueries({ queryKey: ["plates", "reported"] });
      setPlateToDelete(null);
    },
    onError: (err) => {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to delete plate";
      toast.error(msg, { position: "top-right", autoClose: 4000 });
    },
  });

  const handleDeleteClick = (row) => setPlateToDelete(row);
  const handleConfirmDelete = () => {
    if (!plateToDelete) return;
    deletePlateMutation.mutate(plateToDelete.id);
  };
  const handleCloseDeleteModal = () => {
    if (!deletePlateMutation.isPending) setPlateToDelete(null);
  };

  // Fetch reported plates
  const {
    data: platesData,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["plates", "reported"],
    queryFn: () => getPlatesByStatus("reported"),
    retry: 1,
    onError: (err) => {
      const errorMessage =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to fetch reported plates";
      toast.error(errorMessage);
    },
  });

  // One row per plate except status pending / approved / resolved (e.g. reported, rejected)
  const transformedData = useMemo(() => {
    if (!platesData) return [];

    const profiles = platesData.data || platesData.plates || platesData || [];
    const flat = flattenProfilesExcludingPlateStatuses(
      profiles,
      REPORTED_PLATES_EXCLUDED_STATUSES
    );

    const formatDate = (dateString) => {
      if (!dateString) return { date: "N/A", time: "" };
      try {
        const date = new Date(dateString);
        const dateStr = date.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
        const timeStr = date.toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        });
        return { date: dateStr, time: timeStr };
      } catch (e) {
        return { date: dateString, time: "" };
      }
    };

    return flat.map(({ profile, plateEntry, plateId }) => {
      const dateInfo = formatDate(
        profile.createdAt || profile.reportedAt || profile.updatedAt || profile.timestamp
      );

      const userName = getProfileDisplayName(profile);
      const userEmail = getProfileEmail(profile);
      const userImage = getProfileUserImage(profile);

      const plateNumber = getPlateNumber(plateEntry, profile);
      const plateImage =
        getPlateDisplayImageUrl(plateEntry, profile) ||
        process.env.PUBLIC_URL + "/images/uploadedImg.svg";
      const city =
        getPlateLocationState(plateEntry, profile) ||
        profile.location?.city ||
        "N/A";
      const country =
        getPlateLocationCountry(plateEntry, profile) ||
        profile.location?.country ||
        "N/A";

      const reportId = plateId;
      const reason =
        plateEntry.reason ||
        plateEntry.reportReason ||
        getPlateCaption(plateEntry) ||
        profile.reason ||
        profile.reportReason ||
        profile.report?.reason ||
        "N/A";

      const rawStatus = plateEntry?.status
      const hasStatus =
        rawStatus != null && String(rawStatus).trim() !== ""

      let statusLabel = "N/A"
      /** reported = orange pill; status-danger = red (rejected, open, etc.); status-na = missing */
      let statusBadgeModifier = "status-na"

      if (hasStatus) {
        statusLabel = mapPlateStatusLabel(rawStatus)
        const key = String(rawStatus).trim().toLowerCase()
        if (key === "reported") statusBadgeModifier = "reported"
        else statusBadgeModifier = "status-danger"
      }

      const originalData = { ...profile, plateEntry };

      return {
        id: plateId,
        image: userImage,
        name: userName,
        email: userEmail,
        reportId: String(reportId),
        plateNumber,
        plateImage,
        reason,
        datestamp: dateInfo.date,
        timestamp: dateInfo.time,
        city,
        country,
        status: statusLabel,
        statusBadgeModifier,
        originalData,
      };
    });
  }, [platesData]);

  const filteredData = useMemo(() => {
    if (!searchTerm) return transformedData;
    const searchLower = searchTerm.toLowerCase();
    return transformedData.filter(
      (row) =>
        row.name.toLowerCase().includes(searchLower) ||
        row.email.toLowerCase().includes(searchLower) ||
        row.plateNumber.toLowerCase().includes(searchLower) ||
        row.reportId.toLowerCase().includes(searchLower) ||
        row.reason.toLowerCase().includes(searchLower)
    );
  }, [transformedData, searchTerm]);

  const columns = [
    {
      name: "Reported By",
      grow: 3,
      sortable: true,
      cell: (row) => (
        <div className="user-cell">
          <img
            src={row.image}
            alt="user"
            className="user-img"
            onError={(e) => {
              e.target.src = process.env.PUBLIC_URL + "/images/user.svg";
            }}
          />
          <div>
            <strong>{row.name}</strong>
            <br />
            <span>{row.email}</span>
          </div>
        </div>
      ),
    },
    {
      name: "Report ID",
      selector: (row) => row.reportId,
      sortable: true,
    },
    {
      name: "Plate Number",
      selector: (row) => row.plateNumber,
      sortable: true,
    },
    {
      name: "Uploaded Image",
      sortable: false,
      cell: (row) => (
        <div className="plate-image-cell">
          <img
            src={row.plateImage}
            alt="uploaded plate"
            className="plate-img"
            onError={(e) => {
              e.target.src = process.env.PUBLIC_URL + "/images/uploadedImg.svg";
            }}
          />
        </div>
      ),
    },
    {
      name: "Reason",
      sortable: true,
      cell: (row) => {
        const fullReason = row.reason == null ? "" : String(row.reason)
        const { preview, isTruncated } = truncateReasonPreview(fullReason)
        const linkStyle = {
          cursor: "pointer",
          color: "#FF9500",
          textDecoration: "underline",
        }
        const link = (
          <Link
            to="/plates-management/reported-plates/plate-claim-review"
            state={{ plateData: row.originalData, plateId: row.id }}
            className="reason-link"
            style={linkStyle}
          >
            {preview}
          </Link>
        )
        if (!isTruncated || !fullReason.length) return link

        return (
          <OverlayTrigger
            placement="top"
            delay={{ show: 200, hide: 100 }}
            overlay={
              <Tooltip
                id={`reported-reason-tooltip-${row.id}`}
                className="reported-reason-tooltip"
              >
                <span className="reported-reason-tooltip__body">{fullReason}</span>
              </Tooltip>
            }
          >
            <span className="reported-reason-cell">{link}</span>
          </OverlayTrigger>
        )
      },
    },
    {
      name: "Date",
      sortable: true,
      cell: (row) => (
        <div className="timestamp">
          <strong>{row.datestamp}</strong>
          <br />
          <span>{row.timestamp}</span>
        </div>
      ),
    },
    {
      name: "Location",
      sortable: true,
      cell: (row) => (
        <div className="timestamp">
          <strong>{row.city}</strong>
          <br />
          <span>{row.country}</span>
        </div>
      ),
    },
    {
      name: "Status",
      sortable: true,
      selector: (row) => row.status,
      cell: (row) => (
        <span
          className={`status-badge ${row.statusBadgeModifier}`}
          role="status"
        >
          {row.statusBadgeModifier !== "status-na" ? (
            <span className="dot" aria-hidden />
          ) : null}
          {row.status}
        </span>
      ),
    },
    {
      name: "",
      button: true,
      cell: (row) => (
        <div className="actions">
          <Link
            to="/plates-management/reported-plates/plate-claim-review"
            state={{ plateData: row.originalData, plateId: row.id }}
          >
            <img src={process.env.PUBLIC_URL + "/images/edit.svg"} className="me-2" alt="edit" />
          </Link>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteClick(row);
            }}
            disabled={deletePlateMutation.isPending}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: 0,
            }}
            aria-label="Delete plate"
          >
            <img src={process.env.PUBLIC_URL + "/images/trash.svg"} alt="trash" />
          </button>
        </div>
      ),
    },
  ];

  const customStyles = {
    table: {
      style: {
        backgroundColor: "transparent",
        color: "#0D0D0D",
      },
    },
    headRow: {
      style: {
        backgroundColor: "transparent",
        color: "#0D0D0D",
        borderBottom: "1px solid rgba(255,255,255,0.08)",
      },
    },
    headCells: {
      style: {
        fontSize: "10px",
        fontWeight: "500",
        color: "#0D0D0D",
        padding: "12px 10px",
        backgroundColor: "transparent",
      },
    },
    rows: {
      style: {
        backgroundColor: "transparent",
        color: "#0D0D0D",
        fontSize: "10px",
        minHeight: "60px",
        "&:hover": {
          backgroundColor: "#EAE3E0 !important",
          border: "none !important",
          outline: "none !important",
        },
      },
    },
  };

  return (
    <React.Fragment>
      <header className="reported-panel-header items-center gap-3 flex content-justify-between">
        <div className="left-side">
          <h4 className="title">Reported Plates</h4>
        </div>
        <div className="right-side">
          <div className="search_bar">
            <img src={Search} alt="Search" />
            <input
              type="text"
              placeholder="Search for reported plates..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      </header>
      <div className="reportedPlates">
        <div className="card">
          <h4>All Plates</h4>
          {isLoading ? (
            <div className="loading-container">
              <div className="loader"></div>
              <p>Loading reported plates...</p>
            </div>
          ) : isError ? (
            <div className="error-container">
              <p>Error loading reported plates. Please try again.</p>
              {error && (
                <small>
                  {error?.response?.data?.message || error?.message}
                </small>
              )}
            </div>
          ) : (
            <DataTable
              columns={columns}
              data={filteredData}
              pagination
              highlightOnHover
              responsive
              selectableRows
              customStyles={customStyles}
              noDataComponent="No reported plates found"
            />
          )}
        </div>
      </div>

      <ConfirmModal
        isOpen={!!plateToDelete}
        title="Delete plate"
        message={
          plateToDelete
            ? `Are you sure you want to delete plate ${plateToDelete.plateNumber || plateToDelete.id}? This action cannot be undone.`
            : ""
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        isConfirmLoading={deletePlateMutation.isPending}
        onConfirm={handleConfirmDelete}
        onCancel={handleCloseDeleteModal}
      />
    </React.Fragment>
  );
}

export default ReportedPlates;
